// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/**
 * LabelFlow — Master Responsive Layer (2026)
 * ============================================================================
 * The single global stylesheet that makes the whole app adapt from a 320px
 * phone to an ultrawide desktop. Loaded LAST in index.css (after theme.css and
 * theme-overrides.css) so it can adapt the token-themed chrome without
 * re-declaring colors — every value here still comes from tokens.
 *
 * Principles (modern / 2026):
 *   • Mobile-first intent, adaptive max-width queries to retrofit the desktop UI.
 *   • Fluid type & spacing via clamp() — fewer hard breakpoints, smoother scaling.
 *   • Dynamic viewport units (dvh/svh) so 100vh doesn't jump under mobile chrome.
 *   • Safe-area insets for notched / gesture-bar devices.
 *   • Comfortable touch targets on coarse pointers (≥44px, WCAG 2.5.5).
 *   • Never let anything cause horizontal page scroll.
 *   • Respect prefers-reduced-motion & prefers-contrast.
 *
 * Breakpoint scale (max-width, px):
 *   xs ≤ 480   sm ≤ 640   md ≤ 768   lg ≤ 992   xl ≤ 1200
 * Kept numerically aligned with the per-page @media rules already in the app.
 * ========================================================================== */

/* ==========================================================================
   1. FOUNDATIONS — apply everywhere, cheap and safe
   ========================================================================== */

*,
*::before,
*::after {
  box-sizing: border-box;
}

/* The #1 cause of mobile horizontal scroll: a flex/grid child that refuses to
   shrink below its content. Allowing shrink on common layout children fixes it
   without the sledgehammer of \`* { min-width: 0 }\`. */
.row > [class*="col"],
.d-flex > *,
.navbar-nav > *,
[class*="grid"] > * {
  min-width: 0;
}

/* Media never overflows its container. */
img,
svg,
video,
canvas,
audio,
iframe,
embed,
object {
  max-width: 100%;
}
img,
video,
canvas {
  height: auto;
}

/* Long unbroken strings (URLs, IDs, model names) wrap instead of pushing width. */
p,
li,
dd,
dt,
h1, h2, h3, h4, h5, h6,
.card-title,
.badge,
.lf-badge,
td, th {
  overflow-wrap: break-word;
  word-break: break-word;
}

/* Modern text niceties where supported. */
h1, h2, h3 {
  text-wrap: balance;
}
p {
  text-wrap: pretty;
}

/* Belt-and-braces: the page itself must never scroll sideways. */
html,
body {
  max-width: 100%;
  overflow-x: hidden;
}

/* Honour the notch / rounded corners / gesture bar. */
body {
  padding-left: env(safe-area-inset-left);
  padding-right: env(safe-area-inset-right);
}

/* ==========================================================================
   2. FLUID TYPOGRAPHY — scales smoothly, no per-breakpoint font juggling
   ========================================================================== */
:root {
  --fs-h1: clamp(1.5rem, 1.15rem + 1.6vw, 2.25rem);
  --fs-h2: clamp(1.3rem, 1.05rem + 1.2vw, 1.75rem);
  --fs-h3: clamp(1.15rem, 1rem + 0.7vw, 1.375rem);
  --fs-h4: clamp(1.05rem, 0.98rem + 0.4vw, 1.15rem);
  --fs-body: clamp(0.9rem, 0.86rem + 0.2vw, 1rem);
}

h1, .h1 { font-size: var(--fs-h1); line-height: 1.15; }
h2, .h2 { font-size: var(--fs-h2); line-height: 1.2; }
h3, .h3 { font-size: var(--fs-h3); line-height: 1.25; }
h4, .h4 { font-size: var(--fs-h4); line-height: 1.3; }

/* ==========================================================================
   3. RESPONSIVE CONTAINER & SPACING
   ========================================================================== */

/* Page gutter shrinks on small screens, grows on large — fluid, no jumps.
   Named, because a full-height page has to subtract exactly this much. */
:root {
  --lf-shell-gutter: clamp(0.75rem, 0.4rem + 2vw, 1.5rem);
}

.labelflow-container {
  padding: var(--lf-shell-gutter);
  padding-bottom: max(var(--lf-shell-gutter), env(safe-area-inset-bottom));
  width: 100%;
}

/* Page heading + its action buttons: the legacy rule is a no-wrap flex row, so
   a long title and a button fight over the same line and one of them overflows. */
.page-header-title {
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
}
.page-header-title > div {
  flex-wrap: wrap;
}

/* The app shell is ONE flex column: header, then main, then the page.
   It used to be a stack of blocks whose full-height pages each subtracted a
   guessed constant for the chrome above them — \`calc(100vh - 90px)\` in the
   labeling studios, \`- 100px\` in Prediction, \`- 58px\` in the assistant — while
   the header actually measures 57px and this container adds 48px of padding.
   Every one of those guesses was wrong in the same direction, so each of those
   pages ended a few pixels taller than the screen and put a scrollbar on a
   layout designed not to have one. A flex column has to measure nothing. */
.labelflow-theme {
  display: flex;
  flex-direction: column;
  min-height: 100vh;      /* fallback */
  min-height: 100dvh;
}

/* grow into the leftover, never shrink below content: a long page still makes
   the document taller and scrolls normally. */
.labelflow-main,
.pcoded-main-container.labelflow-container {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
}

/* Opt-in for a page that must FIT the viewport rather than flow down it (the
   labeling studios, Prediction, the assistant).

   Why a \`height\` and not just \`flex: 1 1 auto\`: these pages are built around a
   DEFINITE height — \`overflow: hidden\` on the page, \`overflow-y: auto\` on the
   panels inside. Handed an indefinite one, every nested flex item falls back to
   \`min-height: auto\`, refuses to shrink below its content, and pushes the page
   taller than the screen again (measured: +150px on the detection studio).
   So the height is stated — but the chrome above it is MEASURED, never guessed:
   \`--lf-header-h\` is published by Header.jsx from a ResizeObserver, and the
   fallback is only for the first paint. That is the whole bug this replaces:
   four pages each subtracting a different wrong constant. */
.lf-fill-viewport {
  flex: 1 1 auto;
  min-height: 0;
  height: calc(100vh - var(--lf-header-h, 57px) - var(--lf-shell-gutter) * 2);
  height: calc(100dvh - var(--lf-header-h, 57px) - var(--lf-shell-gutter) * 2);
}

/* Below the stacked breakpoint the studios flow and the page scrolls — which is
   the right behaviour on a phone, and what they already switch to themselves. */
@media (max-width: 992px) {
  .lf-fill-viewport {
    height: auto;
    min-height: calc(100dvh - var(--lf-header-h, 57px) - var(--lf-shell-gutter) * 2);
  }
}

/* ==========================================================================
   4. HEADER / TOP NAV
   The header owns its own layout now — see
   src/components/Common/header/styles/header.scss. It stopped using the
   template's navbar markup entirely (.navbar-collapse, .navbar-nav, .m-header,
   .h-drop, .dropdown-menu), so the retrofits that used to live here (undoing a
   fixed 235px logo block, three hard-coded 56px heights, and a grid that lifted
   the two navs out of their wrapper with \`display: contents\`) have nothing left
   to match and are gone with the markup that needed them.
   ========================================================================== */

/* ==========================================================================
   5. GRIDS — give fixed multi-column page grids a sane phone fallback
   Pages set explicit column counts; below md collapse the densest ones so cards
   don't get crushed. Pages with their own breakpoints keep winning by cascade.
   ========================================================================== */
@media (max-width: 768px) {
  .row > [class*="col-"] {
    /* Bootstrap columns already stack; nothing to force — kept as documentation. */
  }
}

/* Reusable, drop-in responsive grid — prefer this for NEW card grids.
   Auto-fits as many columns of ≥ --lf-grid-min (default 240px) as fit. */
.lf-grid-auto {
  display: grid;
  gap: clamp(0.75rem, 2vw, 1.25rem);
  grid-template-columns: repeat(auto-fill, minmax(min(var(--lf-grid-min, 240px), 100%), 1fr));
}

/* ==========================================================================
   6. TABLES & WIDE CONTENT — scroll inside their own box, never the page
   ========================================================================== */
.table-responsive,
.table-responsive-auto {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

@media (max-width: 768px) {
  /* Bare tables (not already wrapped) get their own horizontal scroll so a wide
     table can't blow out the viewport width. */
  .card-body > .table,
  .card-body > table {
    display: block;
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    white-space: nowrap;
  }
}

/* Preformatted / code blocks scroll instead of stretching the layout. */
pre,
code {
  max-width: 100%;
  overflow-x: auto;
}

/* ==========================================================================
   7. MODALS — comfortable sheets on small screens
   ========================================================================== */

/* Every dialog in the app is hand-rolled: the JSX renders \`.modal fade show\`
   with an inline \`display: block\` instead of going through Bootstrap's JS. That
   means \`.modal-open\` never lands on <body>, so the template's
   \`.modal { overflow: hidden }\` is never relaxed and a dialog taller than the
   viewport is CLIPPED with no way to scroll to it — on a short laptop the Add
   New Model form loses its own Cancel/Next buttons. Give the scroll back here,
   globally, rather than per modal. */
.modal.show {
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.modal.show .modal-dialog {
  margin-top: max(1.75rem, env(safe-area-inset-top));
  margin-bottom: max(1.75rem, env(safe-area-inset-bottom));
}

/* Keep the header and footer in view and scroll the body instead — the same
   deal \`.modal-dialog-scrollable\` offers, for dialogs that didn't ask for it. */
.modal.show .modal-content {
  max-height: calc(100vh - 3.5rem);
  max-height: calc(100dvh - 3.5rem);
}
.modal.show .modal-content > .modal-header,
.modal.show .modal-content > .modal-footer {
  flex-shrink: 0;
}
.modal.show .modal-content > .modal-body {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/* On a phone the dialog claims almost the whole screen, so it takes a tighter
   margin than the rule above — hence the same \`.modal.show\` specificity here. */
@media (max-width: 575.98px) {
  .modal.show .modal-dialog,
  .modal-dialog {
    margin: 0.5rem;
    margin-bottom: max(0.5rem, env(safe-area-inset-bottom));
    max-width: calc(100vw - 1rem);
  }
  .modal.show .modal-content,
  .modal-content {
    max-height: calc(100dvh - 1rem);
    overflow: hidden;
  }
  .modal-body {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
  .modal-footer {
    gap: 0.5rem;
  }
  /* Full-width, stacked action buttons are easier to tap. */
  .modal-footer > .btn {
    flex: 1 1 auto;
    margin: 0;
  }
}

/* ==========================================================================
   8. FORMS & CONTROLS — touch ergonomics
   ========================================================================== */

/* Never let inputs overflow their container. */
.form-control,
.form-select,
input,
select,
textarea {
  max-width: 100%;
}

/* On touch / coarse pointers, enforce a comfortable minimum hit area and stop
   iOS from zooming when focusing inputs (needs ≥16px font). */
@media (pointer: coarse) {
  .btn,
  .lf-icon-btn,
  .form-control,
  .form-select,
  .nav-link,
  .dropdown-item,
  a.h-drop {
    min-height: 44px;
  }
  .btn,
  .form-control,
  .form-select {
    font-size: max(1rem, 0.95em);
  }
  .lf-icon-btn {
    width: 44px;
    height: 44px;
  }
}

/* ==========================================================================
   9. RESPONSIVE UTILITIES — small, composable helpers for page code
   ========================================================================== */

/* Stack a flex row into a column below the named breakpoint. */
@media (max-width: 768px) {
  .lf-stack-md {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .lf-stack-md > * {
    width: 100%;
  }
  .lf-full-md {
    width: 100% !important;
  }
  .lf-hide-md {
    display: none !important;
  }
}
@media (max-width: 575.98px) {
  .lf-stack-sm {
    flex-direction: column !important;
    align-items: stretch !important;
  }
  .lf-hide-sm {
    display: none !important;
  }
  .lf-full-sm {
    width: 100% !important;
  }
}
/* Show only on small screens (mirror of the hide helpers). */
@media (min-width: 769px) {
  .lf-only-md {
    display: none !important;
  }
}

/* ==========================================================================
   10. MOTION & ACCESSIBILITY PREFERENCES
   ========================================================================== */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
    scroll-behavior: auto !important;
  }
}

@media (prefers-contrast: more) {
  :root {
    --border: var(--border-strong);
  }
}
`, "",{"version":3,"sources":["webpack://./src/styles/responsive.css"],"names":[],"mappings":"AAAA;;;;;;;;;;;;;;;;;;;;+EAoB+E;;AAE/E;;+EAE+E;;AAE/E;;;EAGE,sBAAsB;AACxB;;AAEA;;sDAEsD;AACtD;;;;EAIE,YAAY;AACd;;AAEA,yCAAyC;AACzC;;;;;;;;EAQE,eAAe;AACjB;AACA;;;EAGE,YAAY;AACd;;AAEA,kFAAkF;AAClF;;;;;;;;;EASE,yBAAyB;EACzB,sBAAsB;AACxB;;AAEA,0CAA0C;AAC1C;EACE,kBAAkB;AACpB;AACA;EACE,iBAAiB;AACnB;;AAEA,iEAAiE;AACjE;;EAEE,eAAe;EACf,kBAAkB;AACpB;;AAEA,sDAAsD;AACtD;EACE,uCAAuC;EACvC,yCAAyC;AAC3C;;AAEA;;+EAE+E;AAC/E;EACE,gDAAgD;EAChD,gDAAgD;EAChD,+CAA+C;EAC/C,iDAAiD;EACjD,+CAA+C;AACjD;;AAEA,UAAU,uBAAuB,EAAE,iBAAiB,EAAE;AACtD,UAAU,uBAAuB,EAAE,gBAAgB,EAAE;AACrD,UAAU,uBAAuB,EAAE,iBAAiB,EAAE;AACtD,UAAU,uBAAuB,EAAE,gBAAgB,EAAE;;AAErD;;+EAE+E;;AAE/E;yEACyE;AACzE;EACE,uDAAuD;AACzD;;AAEA;EACE,+BAA+B;EAC/B,wEAAwE;EACxE,WAAW;AACb;;AAEA;kFACkF;AAClF;EACE,eAAe;EACf,gBAAgB;AAClB;AACA;EACE,eAAe;AACjB;;AAEA;;;;;;;2EAO2E;AAC3E;EACE,aAAa;EACb,sBAAsB;EACtB,iBAAiB,OAAO,aAAa;EACrC,kBAAkB;AACpB;;AAEA;8CAC8C;AAC9C;;EAEE,aAAa;EACb,sBAAsB;EACtB,cAAc;AAChB;;AAEA;;;;;;;;;;;4DAW4D;AAC5D;EACE,cAAc;EACd,aAAa;EACb,2EAA2E;EAC3E,4EAA4E;AAC9E;;AAEA;gFACgF;AAChF;EACE;IACE,YAAY;IACZ,gFAAgF;EAClF;AACF;;AAEA;;;;;;;;;+EAS+E;;AAE/E;;;;+EAI+E;AAC/E;EACE;IACE,+EAA+E;EACjF;AACF;;AAEA;yEACyE;AACzE;EACE,aAAa;EACb,iCAAiC;EACjC,2FAA2F;AAC7F;;AAEA;;+EAE+E;AAC/E;;EAEE,gBAAgB;EAChB,iCAAiC;AACnC;;AAEA;EACE;+CAC6C;EAC7C;;IAEE,cAAc;IACd,WAAW;IACX,gBAAgB;IAChB,iCAAiC;IACjC,mBAAmB;EACrB;AACF;;AAEA,wEAAwE;AACxE;;EAEE,eAAe;EACf,gBAAgB;AAClB;;AAEA;;+EAE+E;;AAE/E;;;;;;qCAMqC;AACrC;EACE,kBAAkB;EAClB,gBAAgB;EAChB,iCAAiC;AACnC;;AAEA;EACE,kDAAkD;EAClD,wDAAwD;AAC1D;;AAEA;gFACgF;AAChF;EACE,gCAAgC;EAChC,iCAAiC;AACnC;AACA;;EAEE,cAAc;AAChB;AACA;EACE,gBAAgB;EAChB,iCAAiC;AACnC;;AAEA;gFACgF;AAChF;EACE;;IAEE,cAAc;IACd,uDAAuD;IACvD,6BAA6B;EAC/B;EACA;;IAEE,+BAA+B;IAC/B,gBAAgB;EAClB;EACA;IACE,gBAAgB;IAChB,iCAAiC;EACnC;EACA;IACE,WAAW;EACb;EACA,0DAA0D;EAC1D;IACE,cAAc;IACd,SAAS;EACX;AACF;;AAEA;;+EAE+E;;AAE/E,+CAA+C;AAC/C;;;;;EAKE,eAAe;AACjB;;AAEA;8DAC8D;AAC9D;EACE;;;;;;;IAOE,gBAAgB;EAClB;EACA;;;IAGE,4BAA4B;EAC9B;EACA;IACE,WAAW;IACX,YAAY;EACd;AACF;;AAEA;;+EAE+E;;AAE/E,+DAA+D;AAC/D;EACE;IACE,iCAAiC;IACjC,+BAA+B;EACjC;EACA;IACE,WAAW;EACb;EACA;IACE,sBAAsB;EACxB;EACA;IACE,wBAAwB;EAC1B;AACF;AACA;EACE;IACE,iCAAiC;IACjC,+BAA+B;EACjC;EACA;IACE,wBAAwB;EAC1B;EACA;IACE,sBAAsB;EACxB;AACF;AACA,6DAA6D;AAC7D;EACE;IACE,wBAAwB;EAC1B;AACF;;AAEA;;+EAE+E;AAC/E;EACE;;;IAGE,sCAAsC;IACtC,uCAAuC;IACvC,uCAAuC;IACvC,gCAAgC;EAClC;AACF;;AAEA;EACE;IACE,8BAA8B;EAChC;AACF","sourcesContent":["/**\n * LabelFlow — Master Responsive Layer (2026)\n * ============================================================================\n * The single global stylesheet that makes the whole app adapt from a 320px\n * phone to an ultrawide desktop. Loaded LAST in index.css (after theme.css and\n * theme-overrides.css) so it can adapt the token-themed chrome without\n * re-declaring colors — every value here still comes from tokens.\n *\n * Principles (modern / 2026):\n *   • Mobile-first intent, adaptive max-width queries to retrofit the desktop UI.\n *   • Fluid type & spacing via clamp() — fewer hard breakpoints, smoother scaling.\n *   • Dynamic viewport units (dvh/svh) so 100vh doesn't jump under mobile chrome.\n *   • Safe-area insets for notched / gesture-bar devices.\n *   • Comfortable touch targets on coarse pointers (≥44px, WCAG 2.5.5).\n *   • Never let anything cause horizontal page scroll.\n *   • Respect prefers-reduced-motion & prefers-contrast.\n *\n * Breakpoint scale (max-width, px):\n *   xs ≤ 480   sm ≤ 640   md ≤ 768   lg ≤ 992   xl ≤ 1200\n * Kept numerically aligned with the per-page @media rules already in the app.\n * ========================================================================== */\n\n/* ==========================================================================\n   1. FOUNDATIONS — apply everywhere, cheap and safe\n   ========================================================================== */\n\n*,\n*::before,\n*::after {\n  box-sizing: border-box;\n}\n\n/* The #1 cause of mobile horizontal scroll: a flex/grid child that refuses to\n   shrink below its content. Allowing shrink on common layout children fixes it\n   without the sledgehammer of `* { min-width: 0 }`. */\n.row > [class*=\"col\"],\n.d-flex > *,\n.navbar-nav > *,\n[class*=\"grid\"] > * {\n  min-width: 0;\n}\n\n/* Media never overflows its container. */\nimg,\nsvg,\nvideo,\ncanvas,\naudio,\niframe,\nembed,\nobject {\n  max-width: 100%;\n}\nimg,\nvideo,\ncanvas {\n  height: auto;\n}\n\n/* Long unbroken strings (URLs, IDs, model names) wrap instead of pushing width. */\np,\nli,\ndd,\ndt,\nh1, h2, h3, h4, h5, h6,\n.card-title,\n.badge,\n.lf-badge,\ntd, th {\n  overflow-wrap: break-word;\n  word-break: break-word;\n}\n\n/* Modern text niceties where supported. */\nh1, h2, h3 {\n  text-wrap: balance;\n}\np {\n  text-wrap: pretty;\n}\n\n/* Belt-and-braces: the page itself must never scroll sideways. */\nhtml,\nbody {\n  max-width: 100%;\n  overflow-x: hidden;\n}\n\n/* Honour the notch / rounded corners / gesture bar. */\nbody {\n  padding-left: env(safe-area-inset-left);\n  padding-right: env(safe-area-inset-right);\n}\n\n/* ==========================================================================\n   2. FLUID TYPOGRAPHY — scales smoothly, no per-breakpoint font juggling\n   ========================================================================== */\n:root {\n  --fs-h1: clamp(1.5rem, 1.15rem + 1.6vw, 2.25rem);\n  --fs-h2: clamp(1.3rem, 1.05rem + 1.2vw, 1.75rem);\n  --fs-h3: clamp(1.15rem, 1rem + 0.7vw, 1.375rem);\n  --fs-h4: clamp(1.05rem, 0.98rem + 0.4vw, 1.15rem);\n  --fs-body: clamp(0.9rem, 0.86rem + 0.2vw, 1rem);\n}\n\nh1, .h1 { font-size: var(--fs-h1); line-height: 1.15; }\nh2, .h2 { font-size: var(--fs-h2); line-height: 1.2; }\nh3, .h3 { font-size: var(--fs-h3); line-height: 1.25; }\nh4, .h4 { font-size: var(--fs-h4); line-height: 1.3; }\n\n/* ==========================================================================\n   3. RESPONSIVE CONTAINER & SPACING\n   ========================================================================== */\n\n/* Page gutter shrinks on small screens, grows on large — fluid, no jumps.\n   Named, because a full-height page has to subtract exactly this much. */\n:root {\n  --lf-shell-gutter: clamp(0.75rem, 0.4rem + 2vw, 1.5rem);\n}\n\n.labelflow-container {\n  padding: var(--lf-shell-gutter);\n  padding-bottom: max(var(--lf-shell-gutter), env(safe-area-inset-bottom));\n  width: 100%;\n}\n\n/* Page heading + its action buttons: the legacy rule is a no-wrap flex row, so\n   a long title and a button fight over the same line and one of them overflows. */\n.page-header-title {\n  flex-wrap: wrap;\n  gap: 0.5rem 1rem;\n}\n.page-header-title > div {\n  flex-wrap: wrap;\n}\n\n/* The app shell is ONE flex column: header, then main, then the page.\n   It used to be a stack of blocks whose full-height pages each subtracted a\n   guessed constant for the chrome above them — `calc(100vh - 90px)` in the\n   labeling studios, `- 100px` in Prediction, `- 58px` in the assistant — while\n   the header actually measures 57px and this container adds 48px of padding.\n   Every one of those guesses was wrong in the same direction, so each of those\n   pages ended a few pixels taller than the screen and put a scrollbar on a\n   layout designed not to have one. A flex column has to measure nothing. */\n.labelflow-theme {\n  display: flex;\n  flex-direction: column;\n  min-height: 100vh;      /* fallback */\n  min-height: 100dvh;\n}\n\n/* grow into the leftover, never shrink below content: a long page still makes\n   the document taller and scrolls normally. */\n.labelflow-main,\n.pcoded-main-container.labelflow-container {\n  display: flex;\n  flex-direction: column;\n  flex: 1 0 auto;\n}\n\n/* Opt-in for a page that must FIT the viewport rather than flow down it (the\n   labeling studios, Prediction, the assistant).\n\n   Why a `height` and not just `flex: 1 1 auto`: these pages are built around a\n   DEFINITE height — `overflow: hidden` on the page, `overflow-y: auto` on the\n   panels inside. Handed an indefinite one, every nested flex item falls back to\n   `min-height: auto`, refuses to shrink below its content, and pushes the page\n   taller than the screen again (measured: +150px on the detection studio).\n   So the height is stated — but the chrome above it is MEASURED, never guessed:\n   `--lf-header-h` is published by Header.jsx from a ResizeObserver, and the\n   fallback is only for the first paint. That is the whole bug this replaces:\n   four pages each subtracting a different wrong constant. */\n.lf-fill-viewport {\n  flex: 1 1 auto;\n  min-height: 0;\n  height: calc(100vh - var(--lf-header-h, 57px) - var(--lf-shell-gutter) * 2);\n  height: calc(100dvh - var(--lf-header-h, 57px) - var(--lf-shell-gutter) * 2);\n}\n\n/* Below the stacked breakpoint the studios flow and the page scrolls — which is\n   the right behaviour on a phone, and what they already switch to themselves. */\n@media (max-width: 992px) {\n  .lf-fill-viewport {\n    height: auto;\n    min-height: calc(100dvh - var(--lf-header-h, 57px) - var(--lf-shell-gutter) * 2);\n  }\n}\n\n/* ==========================================================================\n   4. HEADER / TOP NAV\n   The header owns its own layout now — see\n   src/components/Common/header/styles/header.scss. It stopped using the\n   template's navbar markup entirely (.navbar-collapse, .navbar-nav, .m-header,\n   .h-drop, .dropdown-menu), so the retrofits that used to live here (undoing a\n   fixed 235px logo block, three hard-coded 56px heights, and a grid that lifted\n   the two navs out of their wrapper with `display: contents`) have nothing left\n   to match and are gone with the markup that needed them.\n   ========================================================================== */\n\n/* ==========================================================================\n   5. GRIDS — give fixed multi-column page grids a sane phone fallback\n   Pages set explicit column counts; below md collapse the densest ones so cards\n   don't get crushed. Pages with their own breakpoints keep winning by cascade.\n   ========================================================================== */\n@media (max-width: 768px) {\n  .row > [class*=\"col-\"] {\n    /* Bootstrap columns already stack; nothing to force — kept as documentation. */\n  }\n}\n\n/* Reusable, drop-in responsive grid — prefer this for NEW card grids.\n   Auto-fits as many columns of ≥ --lf-grid-min (default 240px) as fit. */\n.lf-grid-auto {\n  display: grid;\n  gap: clamp(0.75rem, 2vw, 1.25rem);\n  grid-template-columns: repeat(auto-fill, minmax(min(var(--lf-grid-min, 240px), 100%), 1fr));\n}\n\n/* ==========================================================================\n   6. TABLES & WIDE CONTENT — scroll inside their own box, never the page\n   ========================================================================== */\n.table-responsive,\n.table-responsive-auto {\n  overflow-x: auto;\n  -webkit-overflow-scrolling: touch;\n}\n\n@media (max-width: 768px) {\n  /* Bare tables (not already wrapped) get their own horizontal scroll so a wide\n     table can't blow out the viewport width. */\n  .card-body > .table,\n  .card-body > table {\n    display: block;\n    width: 100%;\n    overflow-x: auto;\n    -webkit-overflow-scrolling: touch;\n    white-space: nowrap;\n  }\n}\n\n/* Preformatted / code blocks scroll instead of stretching the layout. */\npre,\ncode {\n  max-width: 100%;\n  overflow-x: auto;\n}\n\n/* ==========================================================================\n   7. MODALS — comfortable sheets on small screens\n   ========================================================================== */\n\n/* Every dialog in the app is hand-rolled: the JSX renders `.modal fade show`\n   with an inline `display: block` instead of going through Bootstrap's JS. That\n   means `.modal-open` never lands on <body>, so the template's\n   `.modal { overflow: hidden }` is never relaxed and a dialog taller than the\n   viewport is CLIPPED with no way to scroll to it — on a short laptop the Add\n   New Model form loses its own Cancel/Next buttons. Give the scroll back here,\n   globally, rather than per modal. */\n.modal.show {\n  overflow-x: hidden;\n  overflow-y: auto;\n  -webkit-overflow-scrolling: touch;\n}\n\n.modal.show .modal-dialog {\n  margin-top: max(1.75rem, env(safe-area-inset-top));\n  margin-bottom: max(1.75rem, env(safe-area-inset-bottom));\n}\n\n/* Keep the header and footer in view and scroll the body instead — the same\n   deal `.modal-dialog-scrollable` offers, for dialogs that didn't ask for it. */\n.modal.show .modal-content {\n  max-height: calc(100vh - 3.5rem);\n  max-height: calc(100dvh - 3.5rem);\n}\n.modal.show .modal-content > .modal-header,\n.modal.show .modal-content > .modal-footer {\n  flex-shrink: 0;\n}\n.modal.show .modal-content > .modal-body {\n  overflow-y: auto;\n  -webkit-overflow-scrolling: touch;\n}\n\n/* On a phone the dialog claims almost the whole screen, so it takes a tighter\n   margin than the rule above — hence the same `.modal.show` specificity here. */\n@media (max-width: 575.98px) {\n  .modal.show .modal-dialog,\n  .modal-dialog {\n    margin: 0.5rem;\n    margin-bottom: max(0.5rem, env(safe-area-inset-bottom));\n    max-width: calc(100vw - 1rem);\n  }\n  .modal.show .modal-content,\n  .modal-content {\n    max-height: calc(100dvh - 1rem);\n    overflow: hidden;\n  }\n  .modal-body {\n    overflow-y: auto;\n    -webkit-overflow-scrolling: touch;\n  }\n  .modal-footer {\n    gap: 0.5rem;\n  }\n  /* Full-width, stacked action buttons are easier to tap. */\n  .modal-footer > .btn {\n    flex: 1 1 auto;\n    margin: 0;\n  }\n}\n\n/* ==========================================================================\n   8. FORMS & CONTROLS — touch ergonomics\n   ========================================================================== */\n\n/* Never let inputs overflow their container. */\n.form-control,\n.form-select,\ninput,\nselect,\ntextarea {\n  max-width: 100%;\n}\n\n/* On touch / coarse pointers, enforce a comfortable minimum hit area and stop\n   iOS from zooming when focusing inputs (needs ≥16px font). */\n@media (pointer: coarse) {\n  .btn,\n  .lf-icon-btn,\n  .form-control,\n  .form-select,\n  .nav-link,\n  .dropdown-item,\n  a.h-drop {\n    min-height: 44px;\n  }\n  .btn,\n  .form-control,\n  .form-select {\n    font-size: max(1rem, 0.95em);\n  }\n  .lf-icon-btn {\n    width: 44px;\n    height: 44px;\n  }\n}\n\n/* ==========================================================================\n   9. RESPONSIVE UTILITIES — small, composable helpers for page code\n   ========================================================================== */\n\n/* Stack a flex row into a column below the named breakpoint. */\n@media (max-width: 768px) {\n  .lf-stack-md {\n    flex-direction: column !important;\n    align-items: stretch !important;\n  }\n  .lf-stack-md > * {\n    width: 100%;\n  }\n  .lf-full-md {\n    width: 100% !important;\n  }\n  .lf-hide-md {\n    display: none !important;\n  }\n}\n@media (max-width: 575.98px) {\n  .lf-stack-sm {\n    flex-direction: column !important;\n    align-items: stretch !important;\n  }\n  .lf-hide-sm {\n    display: none !important;\n  }\n  .lf-full-sm {\n    width: 100% !important;\n  }\n}\n/* Show only on small screens (mirror of the hide helpers). */\n@media (min-width: 769px) {\n  .lf-only-md {\n    display: none !important;\n  }\n}\n\n/* ==========================================================================\n   10. MOTION & ACCESSIBILITY PREFERENCES\n   ========================================================================== */\n@media (prefers-reduced-motion: reduce) {\n  *,\n  *::before,\n  *::after {\n    animation-duration: 0.001ms !important;\n    animation-iteration-count: 1 !important;\n    transition-duration: 0.001ms !important;\n    scroll-behavior: auto !important;\n  }\n}\n\n@media (prefers-contrast: more) {\n  :root {\n    --border: var(--border-strong);\n  }\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
